// Soothing "judge's chambers" soundscape for the intro.
// A warm low drone, faint room tone, soft page-turns on select words, and a muted
// gavel tap that resolves into a gentle chime on the name reveal. No melody, no stabs.

export class CinemaAudio {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.nodes = [];
    this.started = false;
  }

  _ensureContext() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.5;

    this.wet = this.ctx.createGain();
    this.wet.gain.value = 0.4;
    const d1 = this.ctx.createDelay(2.0);
    d1.delayTime.value = 0.21;
    const d2 = this.ctx.createDelay(2.0);
    d2.delayTime.value = 0.37;
    const fb = this.ctx.createGain();
    fb.gain.value = 0.45;
    const tone = this.ctx.createBiquadFilter();
    tone.type = 'lowpass';
    tone.frequency.value = 2800;

    this.wet.connect(d1);
    d1.connect(d2);
    d2.connect(tone);
    tone.connect(this.master);
    tone.connect(fb);
    fb.connect(d1);

    this.master.connect(this.ctx.destination);
    this.nodes.push(this.wet, d1, d2, fb, tone);
  }

  _out(node, dry = 0.7, wet = 0.5) {
    const d = this.ctx.createGain();
    d.gain.value = dry;
    const w = this.ctx.createGain();
    w.gain.value = wet;
    node.connect(d);
    d.connect(this.master);
    node.connect(w);
    w.connect(this.wet);
    this.nodes.push(d, w);
  }

  _noise(seconds) {
    const len = Math.ceil(this.ctx.sampleRate * seconds);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    return src;
  }

  // Warm sustained drone (D major, low register) with a slow-breathing filter
  _drone(start, duration) {
    const freqs = [73.42, 110.0, 146.83, 185.0, 220.0];
    const bus = this.ctx.createGain();
    bus.gain.setValueAtTime(0, start);
    bus.gain.linearRampToValueAtTime(0.11, start + 2.5);
    bus.gain.setValueAtTime(0.11, start + duration - 2.0);
    bus.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 520;
    filter.Q.value = 0.6;

    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = 0.08;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 220;
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    lfo.start(start);
    lfo.stop(start + duration + 0.1);

    freqs.forEach((f, i) => {
      const o = this.ctx.createOscillator();
      o.type = i < 2 ? 'triangle' : 'sine';
      o.frequency.value = f;
      o.detune.value = (i - 2) * 3;
      o.connect(filter);
      o.start(start);
      o.stop(start + duration + 0.1);
      this.nodes.push(o);
    });
    filter.connect(bus);
    this._out(bus, 0.75, 0.45);
    this.nodes.push(filter, bus, lfo, lfoGain);
  }

  // Barely-there room tone (filtered noise) — the hush of a quiet chamber
  _roomTone(start, duration) {
    const src = this._noise(duration + 0.5);
    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 380;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(0.012, start + 2);
    g.gain.setValueAtTime(0.012, start + duration - 1.5);
    g.gain.linearRampToValueAtTime(0, start + duration);
    src.connect(lp);
    lp.connect(g);
    g.connect(this.master);
    src.start(start);
    src.stop(start + duration + 0.1);
    this.nodes.push(src, lp, g);
  }

  // Soft page-turn: short band-passed noise swell
  _pageTurn(start) {
    const src = this._noise(0.6);
    const bp = this.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(1400, start);
    bp.frequency.linearRampToValueAtTime(2600, start + 0.25);
    bp.Q.value = 0.9;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(0.028, start + 0.09);
    g.gain.exponentialRampToValueAtTime(0.0001, start + 0.42);
    src.connect(bp);
    bp.connect(g);
    this._out(g, 0.6, 0.5);
    src.start(start);
    src.stop(start + 0.6);
    this.nodes.push(src, bp, g);
  }

  // Muted wooden gavel tap — low thump plus a soft wood click
  _gavelTap(start, gain = 0.22) {
    const thump = this.ctx.createOscillator();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(210, start);
    thump.frequency.exponentialRampToValueAtTime(95, start + 0.09);
    const tg = this.ctx.createGain();
    tg.gain.setValueAtTime(0, start);
    tg.gain.linearRampToValueAtTime(gain, start + 0.006);
    tg.gain.exponentialRampToValueAtTime(0.0001, start + 0.28);
    thump.connect(tg);
    this._out(tg, 0.8, 0.5);
    thump.start(start);
    thump.stop(start + 0.35);

    const click = this._noise(0.12);
    const bp = this.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1100;
    bp.Q.value = 5;
    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1900;
    const cg = this.ctx.createGain();
    cg.gain.setValueAtTime(0, start);
    cg.gain.linearRampToValueAtTime(gain * 0.55, start + 0.004);
    cg.gain.exponentialRampToValueAtTime(0.0001, start + 0.07);
    click.connect(bp);
    bp.connect(lp);
    lp.connect(cg);
    this._out(cg, 0.7, 0.6);
    click.start(start);
    click.stop(start + 0.12);
    this.nodes.push(thump, tg, click, bp, lp, cg);
  }

  // Gentle bell-like chime with a long tail
  _chime(start, freq, gain = 0.07, duration = 3.2) {
    const partials = [
      { ratio: 1, g: 1.0, type: 'sine' },
      { ratio: 2.76, g: 0.22, type: 'sine' },
      { ratio: 5.4, g: 0.06, type: 'sine' },
    ];
    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0, start);
    env.gain.linearRampToValueAtTime(gain, start + 0.02);
    env.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    partials.forEach((p) => {
      const o = this.ctx.createOscillator();
      o.type = p.type;
      o.frequency.value = freq * p.ratio;
      const g = this.ctx.createGain();
      g.gain.value = p.g;
      o.connect(g);
      g.connect(env);
      o.start(start);
      o.stop(start + duration + 0.1);
      this.nodes.push(o, g);
    });
    this._out(env, 0.5, 0.8);
    this.nodes.push(env);
  }

  start({ panels = [], finalHitMs = 6400, totalMs = 9500 } = {}) {
    if (this.started) return;
    this._ensureContext();
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    this.started = true;

    const now = this.ctx.currentTime + 0.03;
    const dur = totalMs / 1000;

    this._drone(now, dur);
    this._roomTone(now, dur);

    panels.forEach((p, i) => {
      const at = now + p.tMs / 1000;
      if (i % 3 === 1) this._pageTurn(at + 0.05);
      if (i === 4) this._chime(at, 440.0, 0.045, 2.6);   // A4
      if (i === 8) this._chime(at, 587.33, 0.045, 2.6);  // D5
    });

    // Name reveal: two soft gavel taps, then a resolving D-major chime
    const hit = now + finalHitMs / 1000;
    this._gavelTap(hit, 0.2);
    this._gavelTap(hit + 0.3, 0.13);
    this._chime(hit + 0.75, 587.33, 0.075, 3.6);  // D5
    this._chime(hit + 0.95, 880.0, 0.05, 3.4);    // A5
    this._chime(hit + 1.2, 1174.66, 0.03, 3.0);   // D6
  }

  stop(fadeMs = 800) {
    if (!this.ctx || !this.master) return;
    try {
      const now = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(now);
      this.master.gain.setValueAtTime(this.master.gain.value, now);
      this.master.gain.exponentialRampToValueAtTime(0.0001, now + fadeMs / 1000);
      setTimeout(() => {
        try {
          this.nodes.forEach((n) => {
            try { n.disconnect && n.disconnect(); } catch (_) { /* noop */ }
            try { n.stop && n.stop(); } catch (_) { /* noop */ }
          });
          this.nodes = [];
          this.ctx.close().catch(() => {});
        } catch (_) { /* noop */ }
      }, fadeMs + 100);
    } catch (_) { /* noop */ }
  }
}

export default CinemaAudio;
